import { useUnit } from 'effector-react';
import { Copy, Link2, Loader2, Share2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { $openSync, importConfirmed } from '../../entities/library';
import { toastShown } from '../../entities/notifications';
import { $project } from '../../entities/project';
import { shareUrl } from '../../shared/api/shares';
import { router, routes } from '../../shared/routing';
import {
  Button,
  Dialog,
  DialogContent,
  Field,
  Input,
  Select,
  Switch,
  Tooltip,
} from '../../shared/ui';
import {
  $busy,
  $includeView,
  $inAccount,
  $language,
  $link,
  $shareDialogOpen,
  $shareError,
  createRequested,
  includeViewChanged,
  languageChanged,
  linkQuery,
  resetRequested,
  shareDialogClosed,
  shareDialogOpened,
  stopRequested,
  type ShareLanguage,
} from './model';

/** Top-bar button that opens the share dialog. */
export function ShareButton() {
  const { t } = useTranslation();
  const open = useUnit(shareDialogOpened);
  return (
    <Tooltip content={t('share.hint')}>
      <Button size="sm" variant="ghost" leadingIcon={<Share2 />} onClick={open}>
        {t('share.button')}
      </Button>
    </Tooltip>
  );
}

export function ShareDialog() {
  const { t } = useTranslation();
  const [open, close, project] = useUnit([$shareDialogOpen, shareDialogClosed, $project]);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      {open && project && (
        <DialogContent
          title={t('share.title', { name: project.name })}
          description={t('share.description')}
          closeLabel={t('common.close')}
        >
          <ShareBody projectId={project.id} />
        </DialogContent>
      )}
    </Dialog>
  );
}

function ShareBody({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const [inAccount, link, error, busy, create] = useUnit([
    $inAccount,
    $link,
    $shareError,
    $busy,
    createRequested,
  ]);

  if (!inAccount) return <NotInAccount projectId={projectId} />;
  return (
    <div className="space-y-3">
      {error && (
        <p role="alert" className="rounded-md bg-bg-3 px-2.5 py-2 text-sm text-text-2">
          {t(`share.errors.${error}`)}
        </p>
      )}
      {link === 'unknown' && busy && (
        <p role="status" className="flex items-center gap-2 text-sm text-text-2">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          {t('share.loading')}
        </p>
      )}
      {link === null && (
        <div className="space-y-2">
          <p className="text-sm text-text-2">{t('share.none')}</p>
          <Button variant="primary" leadingIcon={<Link2 />} disabled={busy} onClick={create}>
            {t('share.create')}
          </Button>
        </div>
      )}
      {link !== null && link !== 'unknown' && <ActiveLink token={link.token} />}
    </div>
  );
}

/** Signed out, or a project only in this browser: sharing needs the account's copy. */
function NotInAccount({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const [sync, addToAccount, signIn] = useUnit([
    $openSync,
    importConfirmed,
    routes.signIn.navigate,
  ]);
  if (sync === 'none') {
    return (
      <div className="space-y-2">
        <p className="text-sm text-text-2">{t('share.signInFirst')}</p>
        <Button
          variant="primary"
          onClick={() => {
            void signIn({
              params: {},
              query: { returnTo: `${window.location.pathname}${window.location.search}` },
            });
          }}
        >
          {t('account.signIn')}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-sm text-text-2">{t('share.localOnly')}</p>
      <Button
        variant="primary"
        onClick={() => {
          addToAccount([projectId]);
        }}
      >
        {t('home.addToAccount')}
      </Button>
    </div>
  );
}

const LANGUAGES: readonly ShareLanguage[] = ['viewer', 'en', 'bg'];

function ActiveLink({ token }: { token: string }) {
  const { t } = useTranslation();
  const [language, includeView, query, busy] = useUnit([
    $language,
    $includeView,
    router.$query,
    $busy,
  ]);
  const [setLanguage, setIncludeView, reset, stop, toast] = useUnit([
    languageChanged,
    includeViewChanged,
    resetRequested,
    stopRequested,
    toastShown,
  ]);
  const [confirming, setConfirming] = useState<'reset' | 'stop' | null>(null);
  const url = shareUrl(window.location.origin, token, linkQuery(query, language, includeView));

  const copy = (): void => {
    void navigator.clipboard.writeText(url).then(
      () => {
        toast({ message: 'share.copied' });
      },
      () => {
        toast({ message: 'share.copyFailed', tone: 'error' });
      },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        <Input
          readOnly
          aria-label={t('share.link')}
          value={url}
          className="font-mono text-xs"
          onFocus={(e) => {
            e.currentTarget.select();
          }}
        />
        <Button variant="primary" leadingIcon={<Copy />} onClick={copy}>
          {t('share.copy')}
        </Button>
      </div>
      <Field label={t('share.language')}>
        {({ id }) => (
          <Select<ShareLanguage>
            id={id}
            value={language}
            options={LANGUAGES.map((l) => ({
              value: l,
              label:
                l === 'viewer'
                  ? t('share.languageViewer')
                  : l === 'en'
                    ? t('settings.languageEn')
                    : t('settings.languageBg'),
            }))}
            onValueChange={setLanguage}
          />
        )}
      </Field>
      <Field label={t('share.includeView')} hint={t('share.includeViewHint')}>
        {({ id, describedBy }) => (
          <div className="pt-1">
            <Switch
              id={id}
              aria-describedby={describedBy}
              checked={includeView}
              onCheckedChange={setIncludeView}
            />
          </div>
        )}
      </Field>
      <p className="text-xs text-text-3">{t('share.latestNote')}</p>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-subtle pt-3">
        {confirming ? (
          <>
            <span className="mr-auto text-xs text-text-2">
              {t(confirming === 'reset' ? 'share.confirmReset' : 'share.confirmStop')}
            </span>
            <Button
              variant="ghost"
              onClick={() => {
                setConfirming(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => {
                if (confirming === 'reset') reset();
                else stop();
                setConfirming(null);
              }}
            >
              {t(confirming === 'reset' ? 'share.reset' : 'share.stop')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setConfirming('reset');
              }}
            >
              {t('share.reset')}
            </Button>
            <Button
              variant="ghost"
              className="hover:text-negative"
              disabled={busy}
              onClick={() => {
                setConfirming('stop');
              }}
            >
              {t('share.stop')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
